// What a board card reads from the record and the attachments, beyond the issue itself (Poulpe-741): its landed
// commit and its count of comments and attachments. Each index is built once per map the store hands out, not once
// per card: the store replaces a map whenever it changes, so a map seen before has the same facts.
import type { BoardAttachment, Issue, RecordEntry } from '../api/types'
import { githubRepository } from '../lib/releases'

export interface RecordFacts {
  /** The commit its work landed as, or null when its record names none. */
  landed: string | null
  /** Its record's discussion entries: the comments people and agents wrote. */
  comments: number
}

const NONE: RecordFacts = { landed: null, comments: 0 }
const SHA = /^[0-9a-f]{40}$/

const commitOf = (entry: RecordEntry) => entry.refs?.find(ref => ref.kind === 'commit' && SHA.test(ref.id))?.id ?? null

/** Poulpe's own entry when the queue moves main: "Landed: main is at <sha>, by a fast-forward." (queue/transitions.rs). */
const queueLanded = (entry: RecordEntry) => entry.kind === 'landing' && entry.author.kind === 'poulpe' && entry.body.startsWith('Landed:')
/**
 * A person's or an agent's evidence naming a commit: how a lead records a landing by hand ("Landed by hand on main as
 * merge <sha>"). Poulpe's own evidence names candidates and checked commits, which may never land, so it is left out;
 * so are "Proposed <sha>" landing entries, whose head is not what main moved to.
 */
const handEvidence = (entry: RecordEntry) => entry.kind === 'evidence' && entry.author.kind !== 'poulpe'

const recordIndex = new WeakMap<ReadonlyMap<string, RecordEntry>, Map<string, RecordFacts>>()

export function recordFacts(entries: ReadonlyMap<string, RecordEntry>): ReadonlyMap<string, RecordFacts> {
  const known = recordIndex.get(entries)
  if (known) return known
  // The newest of each kind wins, by its place in the record.
  const queue = new Map<string, { number: number; commit: string }>()
  const hand = new Map<string, { number: number; commit: string }>()
  const comments = new Map<string, number>()
  const keep = (into: typeof queue, entry: RecordEntry, commit: string) => {
    const best = into.get(entry.issue)
    if (!best || best.number < entry.number) into.set(entry.issue, { number: entry.number, commit })
  }
  for (const entry of entries.values()) {
    if (entry.kind === 'discussion' && !entry.system) comments.set(entry.issue, (comments.get(entry.issue) ?? 0) + 1)
    const commit = commitOf(entry)
    if (!commit) continue
    if (queueLanded(entry)) keep(queue, entry, commit)
    else if (handEvidence(entry)) keep(hand, entry, commit)
  }
  const facts = new Map<string, RecordFacts>()
  for (const issue of new Set([...queue.keys(), ...hand.keys(), ...comments.keys()])) {
    facts.set(issue, { landed: queue.get(issue)?.commit ?? hand.get(issue)?.commit ?? null, comments: comments.get(issue) ?? 0 })
  }
  recordIndex.set(entries, facts)
  return facts
}

export const recordFactsOf = (entries: ReadonlyMap<string, RecordEntry>, issue: string): RecordFacts => recordFacts(entries).get(issue) ?? NONE

const attachmentIndex = new WeakMap<ReadonlyMap<string, BoardAttachment>, Map<string, number>>()

/** How many files are attached to the issue itself (not to its plan, an entry or a comment). */
export function attachmentCount(attachments: ReadonlyMap<string, BoardAttachment>, issue: string): number {
  let counts = attachmentIndex.get(attachments)
  if (!counts) {
    counts = new Map()
    for (const file of attachments.values()) if (file.issue) counts.set(file.issue, (counts.get(file.issue) ?? 0) + 1)
    attachmentIndex.set(attachments, counts)
  }
  return counts.get(issue) ?? 0
}

/** The landed commit's page on GitHub, for an issue On Main or Done whose project names its repository. */
export function landedCommitUrl(issue: Pick<Issue, 'state'>, landed: string | null, repository: string | null | undefined): string | null {
  if (!landed || (issue.state !== 'on_main' && issue.state !== 'done')) return null
  const root = githubRepository(repository ?? undefined)
  return root ? `${root}/commit/${landed}` : null
}
